import React, { useState, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Message as MessageType, Conversation } from '../types';

export interface MessageProps {
  message: MessageType;
  modelName?: string;
  provider?: string;
  onBranchFromSelection?: (messageId: string, excerpt: string) => void;
  subchats?: Conversation[];
  onOpenSubchat?: (subchat: Conversation) => void;
}

const providerColors: Record<string, string> = {
  openai: 'bg-black text-white',
  anthropic: 'bg-[#cc785c] text-white',
  google: 'bg-white text-gray-700 border border-gray-200 dark:border-gray-600',
  mistral: 'bg-[#FF7000] text-white',
  deepseek: 'bg-[#4D6BFE] text-white',
};

const ProviderLogo: React.FC<{ provider: string }> = ({ provider }) => {
  switch (provider.toLowerCase()) {
    case 'openai':
      return (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.843-3.372L15.115 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.403-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
        </svg>
      );
    case 'anthropic':
      return (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.304 3.541 13.766 13.6h7.082zM6.696 3.541 0 20.459h5.22l1.389-3.754h7.104l1.389 3.754H20.32L13.627 3.541z"/>
        </svg>
      );
    case 'google':
      return (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      );
    case 'mistral':
      return (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
          <rect x="0" y="0" width="5.5" height="5.5"/>
          <rect x="9.25" y="0" width="5.5" height="5.5"/>
          <rect x="18.5" y="0" width="5.5" height="5.5"/>
          <rect x="0" y="9.25" width="5.5" height="5.5"/>
          <rect x="9.25" y="9.25" width="5.5" height="5.5"/>
          <rect x="18.5" y="9.25" width="5.5" height="5.5"/>
          <rect x="0" y="18.5" width="5.5" height="5.5"/>
          <rect x="9.25" y="18.5" width="5.5" height="5.5"/>
        </svg>
      );
    case 'deepseek':
      return (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 3a7 7 0 1 1 0 14A7 7 0 0 1 12 5zm0 2a5 5 0 1 0 0 10A5 5 0 0 0 12 7zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"/>
        </svg>
      );
    default:
      return (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
  }
};

const CopyButton: React.FC<{ text: string; className?: string }> = ({ text, className = '' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${className}`}
      title={copied ? 'Copied!' : 'Copy'}
    >
      {copied ? (
        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );
};

const SubchatPill: React.FC<{ subchats: Conversation[]; onOpenSubchat?: (subchat: Conversation) => void }> = ({
  subchats,
  onOpenSubchat,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  if (subchats.length === 0) return null;

  const handleClick = () => {
    if (subchats.length === 1) {
      onOpenSubchat?.(subchats[0]);
    } else {
      setPickerOpen((prev) => !prev);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
        title={subchats.length === 1 ? 'Open subchat' : 'View subchats'}
      >
        💬 {subchats.length} {subchats.length === 1 ? 'thread' : 'threads'}
      </button>

      {pickerOpen && (
        <div className="absolute z-20 bottom-full mb-1 left-0 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 max-h-56 overflow-y-auto">
          {subchats.map((subchat) => (
            <button
              key={subchat.id}
              onClick={() => {
                setPickerOpen(false);
                onOpenSubchat?.(subchat);
              }}
              className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 truncate"
              title={subchat.sourceExcerpt}
            >
              {subchat.sourceExcerpt || subchat.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Message: React.FC<MessageProps> = ({
  message,
  modelName,
  provider,
  onBranchFromSelection,
  subchats,
  onOpenSubchat,
}) => {
  const isUser = message.role === 'user';
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [selectionButton, setSelectionButton] = useState<{ text: string; x: number; y: number } | null>(null);

  const handleMouseUp = useCallback(() => {
    if (!onBranchFromSelection) return;

    const selection = window.getSelection();
    const text = selection?.toString().trim();

    if (!text || !selection || selection.rangeCount === 0 || !bubbleRef.current?.contains(selection.anchorNode)) {
      setSelectionButton(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    setSelectionButton({ text, x: rect.left + rect.width / 2, y: rect.top });
  }, [onBranchFromSelection]);

  const handleAskAboutThis = useCallback(() => {
    if (!selectionButton || !onBranchFromSelection) return;
    onBranchFromSelection(message.id, selectionButton.text);
    setSelectionButton(null);
    window.getSelection()?.removeAllRanges();
  }, [selectionButton, onBranchFromSelection, message.id]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24 && date.getDate() === now.getDate()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diffInHours < 168) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (isUser) {
    return (
      <div className="flex justify-end py-2" data-testid={`message-${message.id}`} data-role="user">
        <div className="max-w-[75%] group">
          <div className="bg-blue-600 text-white rounded-2xl rounded-br-md px-4 py-3 shadow-sm">
            <div className="whitespace-pre-wrap break-words text-sm">
              {message.content}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-1 px-1">
            <span className="text-xs text-gray-400 dark:text-gray-500" data-testid="message-timestamp">
              {formatTimestamp(message.timestamp)}
            </span>
            <CopyButton text={message.content} className="opacity-0 group-hover:opacity-100" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start py-2" data-testid={`message-${message.id}`} data-role="assistant">
      <div className="max-w-[85%] group">
        {/* Model badge */}
        {modelName && (
          <div className="flex items-center gap-1.5 mb-1 px-1">
            <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${provider ? (providerColors[provider.toLowerCase()] ?? 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white') : 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white'}`}>
              <ProviderLogo provider={provider ?? ''} />
            </div>
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400" data-testid="message-model">
              {modelName}
            </span>
          </div>
        )}

        <div
          ref={bubbleRef}
          onMouseUp={handleMouseUp}
          className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm"
        >
          {selectionButton && (
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              onClick={handleAskAboutThis}
              className="fixed z-30 -translate-x-1/2 -translate-y-full -mt-2 px-3 py-1.5 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-xs font-medium shadow-lg hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors whitespace-nowrap"
              style={{ left: selectionButton.x, top: selectionButton.y }}
            >
              💬 Ask about this
            </button>
          )}
          <div className="prose prose-sm max-w-none dark:prose-invert" data-testid="message-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => (
                  <p className="mb-3 last:mb-0 leading-relaxed">{children}</p>
                ),
                code: ({ inline, className, children, ...props }: any) => {
                  const codeString = String(children).replace(/\n$/, '');
                  const language = className?.replace('language-', '') || '';

                  if (inline) {
                    return (
                      <code
                        className="px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-pink-600 dark:text-pink-400 text-xs font-mono"
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  }

                  return (
                    <div className="relative group/code my-3 -mx-1">
                      <div className="flex items-center justify-between bg-gray-800 dark:bg-gray-900 text-gray-300 px-4 py-2 rounded-t-lg text-xs">
                        <span className="font-mono">{language || 'code'}</span>
                        <CopyButton text={codeString} />
                      </div>
                      <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 rounded-b-lg p-4 overflow-x-auto text-xs leading-relaxed m-0">
                        <code className={className} {...props}>
                          {children}
                        </code>
                      </pre>
                    </div>
                  );
                },
                pre: ({ children }) => <>{children}</>,
                ul: ({ children }) => (
                  <ul className="list-disc list-outside ml-4 mb-3 space-y-1">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal list-outside ml-4 mb-3 space-y-1">{children}</ol>
                ),
                li: ({ children }) => (
                  <li className="leading-relaxed">{children}</li>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-blue-300 dark:border-blue-700 pl-4 my-3 italic text-gray-600 dark:text-gray-400">
                    {children}
                  </blockquote>
                ),
                a: ({ children, href }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 underline underline-offset-2 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    {children}
                  </a>
                ),
                h1: ({ children }) => (
                  <h1 className="text-xl font-bold mb-3 mt-4 first:mt-0 text-gray-900 dark:text-white">{children}</h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-lg font-bold mb-2 mt-4 first:mt-0 text-gray-900 dark:text-white">{children}</h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-base font-semibold mb-2 mt-3 first:mt-0 text-gray-900 dark:text-white">{children}</h3>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto my-3 rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">{children}</table>
                  </div>
                ),
                th: ({ children }) => (
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="px-3 py-2 text-sm border-t border-gray-100 dark:border-gray-700">{children}</td>
                ),
                hr: () => (
                  <hr className="my-4 border-gray-200 dark:border-gray-700" />
                ),
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        </div>

        {/* Metadata footer */}
        <div className="flex items-center gap-2 mt-1 px-1 text-xs text-gray-400 dark:text-gray-500">
          <span data-testid="message-timestamp">{formatTimestamp(message.timestamp)}</span>
          {message.tokenCount > 0 && (
            <>
              <span>·</span>
              <span data-testid="message-tokens">{message.tokenCount} tokens</span>
            </>
          )}
          <CopyButton text={message.content} className="opacity-0 group-hover:opacity-100" />
          {subchats && subchats.length > 0 && (
            <SubchatPill subchats={subchats} onOpenSubchat={onOpenSubchat} />
          )}
        </div>
      </div>
    </div>
  );
};
